import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, Flex, Text } from '@radix-ui/themes';
import { useSelector } from 'react-redux';
import { selectUserSelectedLocal, selectUserSelectedLocalCode, selectDevice } from 'store/slices/common';

import SubSlider from 'components/common/slider/SubSlider';
import NoData from 'components/common/noData';

import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const CenterIntroduction = ({ localData }) => {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';

  // hooks
  const userSelectedLocal = useSelector(selectUserSelectedLocal);
  const userSelectedLocalCode = useSelector(selectUserSelectedLocalCode);

  // state
  const [currentIndexes, setCurrentIndexes] = useState(localData?.childCenter?.map(() => 0) || []);

  // 슬라이드 이동 method
  const handleSlideChange = (index, activeIndex) => {
    setCurrentIndexes(prevIndexes => {
      if (!Array.isArray(prevIndexes)) {
        prevIndexes = [];
      }

      const newIndexes = [...prevIndexes];
      newIndexes[index] = activeIndex;
      return newIndexes;
    });
  };

  useEffect(() => {
    if (localData?.childCenter) {
      setCurrentIndexes(localData.childCenter.map(() => 0));
    }
  }, [localData?.childCenter]);

  // 전남 센터소개 에디터 html 스와이프 적용
  useEffect(() => {
    if (isMobile) {
      Swiper.use([Pagination]);

      const eduSwiperEl = document.querySelector('.edu-wrap.swiper');
      if (eduSwiperEl) {
        new Swiper(eduSwiperEl, {
          slidesPerView: 1.08,
          loop: true,
          pagination: {
            el: '.ex-page.swiper-pagination',
            clickable: true
          }
        });
      }

      const exSwiperEl = document.querySelector('.ex-wrap.swiper');
      if (exSwiperEl) {
        new Swiper(exSwiperEl, {
          slidesPerView: 1.08,
          loop: true,
          pagination: {
            el: '.ex-page.swiper-pagination',
            clickable: true
          }
        });
      }
    }
  }, []);

  return localData ? (
    <>
      <Box mt={isMobile ? 'var(--space-6)' : 'var(--space-12)'} mb={isMobile ? 'var(--space-6)' : 'var(--space-15)'}>
        <Flex justify="between">
          {localData.basicInfo ? (
            <ul className="info-items">
              {Object.entries(localData.basicInfo)
                // address, phone, opentime만 필터링하고 정렬
                .filter(([key]) => ['address', 'phone', 'opentime'].includes(key))
                .sort(([keyA], [keyB]) => {
                  const order = { address: 1, opentime: 2, phone: 3 }; // 순서 정의
                  return (order[keyA] || Infinity) - (order[keyB] || Infinity); // 정의된 순서대로 정렬
                })
                .map(([key, value], index) => {
                  // 클래스와 타이틀을 매핑
                  const classMap = {
                    address: 'address',
                    phone: 'call',
                    opentime: 'time'
                  };
                  const titleMap = {
                    address: '주소',
                    phone: '전화번호',
                    opentime: '오픈시간'
                  };

                  return (
                    <li key={index} className={`body1 ${classMap[key] || ''}`}>
                      <Flex direction={isMobile && 'column'} align="start">
                        <Text className="title">{titleMap[key] || ''}</Text>
                        <Text as="p" className={`body${isMobile ? '2' : '1'}`} wrap={'balance'}>
                          {value || '-'}
                        </Text>
                      </Flex>
                    </li>
                  );
                })}
            </ul>
          ) : (
            <Text as="p">센터 정보가 없습니다.</Text>
          )}
        </Flex>
      </Box>

      <Box mb={isMobile ? 'var(--space-9)' : 'var(--space-20)'} px={isMobile && 'var(--space-4)'}>
        <Flex direction={isMobile && 'column'} justify="between" gap={isMobile && 'var(--space-2)'}>
          {localData.additionalInfo.map((item, index) => (
            <Card
              key={index}
              className={`info-wrap ${index === 0 ? 'light-blue' : index === 1 ? 'light-yellow' : index === 2 ? 'light-red' : ''}`}
            >
              <Text as="p" className={isMobile ? 'body2 font-bold' : 'sub-title'}>
                {item.category}
              </Text>
              <ul className="info-items info-items-block">
                <li className={`body${isMobile ? '2' : '1'} call`}>
                  <span className="hidden">전화번호</span>
                  {item.phone || '-'}
                </li>
                <li className={`body${isMobile ? '2' : '1'} mail`}>
                  <span className="hidden">이메일</span>
                  {item.email || '-'}
                </li>
              </ul>
            </Card>
          ))}
        </Flex>
      </Box>

      {/* 컨텐츠 CK 에디터 데이터 */}
      <Box mb={isMobile ? 'var(--space-9)' : 'var(--space-20)'} px={isMobile && 'var(--space-4)'}>
        <Box dangerouslySetInnerHTML={{ __html: localData.business }} />
      </Box>
      {/* // 컨텐츠 CK 에디터 데이터 */}

      {localData.childCenter.map((item, index) => (
        <Box className="slider-wrap" key={index}>
          <Flex direction={isMobile && 'column'} justify="between">
            <Box className="slider-text">
              <Box className="center-info">
                {!isMobile && (
                  <Box className="image-wrap">
                    <img
                      src={require(`../../assets/images/logo/logo-${userSelectedLocalCode}.svg`)}
                      alt={`${userSelectedLocal}센터 sw미래채움 로고`}
                    />
                  </Box>
                )}
                <Text as="p" className={isMobile ? 'sub-title' : 'title4'}>
                  {item.centerName}
                </Text>
                <Text as="p" className={`body${isMobile ? '2' : '1'}`}>
                  {item.content}
                </Text>
              </Box>
              {isMobile && (
                <SubSlider
                  name={item.centerName}
                  item={item.files}
                  index={index}
                  currentIndex={currentIndexes[index] || 0}
                  handleSlideChange={handleSlideChange}
                />
              )}
              <Box className="info-text">
                <React.Fragment>
                  <Text as="p" className={`body${isMobile ? '2' : '1'} font-bold`}>
                    {item.files[currentIndexes[index]]?.fileDisplayName}
                  </Text>
                  <Text as="p" className={`body${isMobile ? '3' : '2'}`}>
                    {item.files[currentIndexes[index]]?.fileDesc}
                  </Text>
                </React.Fragment>
              </Box>
            </Box>
            {/* slide의 indicator 개발 부탁 드립니다. */}
            {!isMobile && (
              <SubSlider
                name={item.centerName}
                item={item.files}
                index={index}
                currentIndex={currentIndexes[index] || 0}
                handleSlideChange={handleSlideChange}
              />
            )}
          </Flex>
        </Box>
      ))}
    </>
  ) : (
    <NoData />
  );
};

export default CenterIntroduction;
